import { motion } from 'framer-motion';
import { Diamond, Handshake, TrendingUp, Users, Target, Award, CheckCircle, ArrowRight, Star, Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';

const AffiliatePage = () => {
  const containerVariants = { 
    hidden: { opacity: 0 }, 
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } } 
  };
  
  const itemVariants = { 
    hidden: { opacity: 0, y: 30 }, 
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } 
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="affiliate-page">
      <div className="glow-blob glow-blob-purple top-40 -right-48 fixed" />
      <div className="glow-blob glow-blob-green bottom-40 -left-48 fixed" />

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <motion.section 
          className="text-center mb-16" 
          variants={containerVariants} 
          initial="hidden" 
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 mb-4">
            <Diamond className="w-5 h-5 text-[#39FF14] sparkle" />
            <span className="text-[#39FF14] font-medium uppercase tracking-wider text-sm">Partner With Me</span>
            <Diamond className="w-5 h-5 text-[#39FF14] sparkle" />
          </motion.div>
          
          <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase mb-6">
            <span className="text-[#E0E0E0]">Affiliate</span>
            <br />
            <span className="text-glow-purple text-[#9D4EDD]">Partnerships</span>
          </motion.h1>
          
          <motion.p variants={itemVariants} className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
            I partner with brands to create authentic, engaging content that resonates with my community. 
            Let us work together to showcase your products and services to an audience that trusts my recommendations.
          </motion.p>
        </motion.section>

        {/* Stats */}
        <motion.section 
          className="mb-20" 
          variants={containerVariants} 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="glass-card p-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="stat-number text-3xl md:text-4xl">10K+</div>
              <div className="text-gray-400 uppercase tracking-wider text-xs md:text-sm">Community Members</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-3xl md:text-4xl">500+</div>
              <div className="text-gray-400 uppercase tracking-wider text-xs md:text-sm">Hours Streamed</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-3xl md:text-4xl">20+</div>
              <div className="text-gray-400 uppercase tracking-wider text-xs md:text-sm">Brand Campaigns</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-3xl md:text-4xl">50K+</div>
              <div className="text-gray-400 uppercase tracking-wider text-xs md:text-sm">Social Reach</div>
            </div>
          </motion.div>
        </motion.section>

        {/* Why Partner */}
        <motion.section 
          className="mb-20" 
          variants={containerVariants} 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase mb-4">
              <span className="text-[#E0E0E0]">Why Partner</span>{' '}
              <span className="text-[#9D4EDD]">With Me?</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              When you partner with RelatableShayla, you get more than just promotion - you get authentic advocacy.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div variants={itemVariants} className="service-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#9D4EDD]/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-[#9D4EDD]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2 text-[#E0E0E0]">Engaged Community</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">Access to The Relatable Family - a loyal, active community of 10K+ players who trust my recommendations.</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="service-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#9D4EDD]/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-[#9D4EDD]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2 text-[#E0E0E0]">Authentic Promotion</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">Genuine product showcases through live streams, social media content, and community engagement.</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="service-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#9D4EDD]/20 flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-[#9D4EDD]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2 text-[#E0E0E0]">Targeted Reach</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">Direct access to social casino enthusiasts and gaming community members actively seeking new platforms.</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="service-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#9D4EDD]/20 flex items-center justify-center flex-shrink-0">
                  <Megaphone className="w-6 h-6 text-[#9D4EDD]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2 text-[#E0E0E0]">Multi-Platform Exposure</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">Promotion across YouTube, TikTok, Instagram, Kick, Twitch, and Discord for maximum visibility.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Partnership Services */}
        <motion.section 
          className="mb-20" 
          variants={containerVariants} 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase mb-4">
              <span className="text-[#E0E0E0]">Partnership</span>{' '}
              <span className="text-[#39FF14]">Services</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Flexible partnership options tailored to meet your brand goals and budget.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div variants={itemVariants} className="glass-card p-6 hover:border-[#39FF14] transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-[#39FF14]" />
                <h3 className="text-lg font-bold text-[#E0E0E0]">Live Stream Sponsorships</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">Featured gameplay sessions showcasing your platform with real-time audience engagement.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Dedicated stream sessions</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Live Q and A</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Viewer interaction</li>
              </ul>
            </motion.div>
            
            <motion.div variants={itemVariants} className="glass-card p-6 hover:border-[#39FF14] transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-[#39FF14]" />
                <h3 className="text-lg font-bold text-[#E0E0E0]">Affiliate Code Promotions</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">Custom referral codes and links shared across all platforms with trackable metrics.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Unique promo codes</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Tracked conversions</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Community incentives</li>
              </ul>
            </motion.div>
            
            <motion.div variants={itemVariants} className="glass-card p-6 hover:border-[#39FF14] transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-[#39FF14]" />
                <h3 className="text-lg font-bold text-[#E0E0E0]">UGC and Content Creation</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">Professional user-generated content including videos, posts, and stories for your brand.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Custom video content</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Social media posts</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Review content</li>
              </ul>
            </motion.div>
            
            <motion.div variants={itemVariants} className="glass-card p-6 hover:border-[#39FF14] transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-[#39FF14]" />
                <h3 className="text-lg font-bold text-[#E0E0E0]">Community Giveaways</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">Sponsored giveaways that drive engagement and introduce your brand to new customers.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Branded giveaways</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Community engagement</li>
                <li className="flex items-center gap-2 text-sm text-gray-300"><CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />Social sharing boost</li>
              </ul>
            </motion.div>
          </div>
        </motion.section>

        {/* Current Partners */}
        <motion.section 
          className="mb-20" 
          variants={containerVariants} 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase mb-4">
              <span className="text-[#E0E0E0]">Current</span>{' '}
              <span className="text-[#9D4EDD]">Partners</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Proud to work with these amazing brands in the social gaming space.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div variants={itemVariants} className="glass-card overflow-hidden">
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-1/3 aspect-square sm:aspect-auto">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ugc-marketing-brand/artifacts/5ozl958l_ChatGPT%20Image%20Dec%2018%2C%202025%2C%2005_36_06%20PM.png" 
                    alt="Legendz Casino" 
                    className="w-full h-full object-cover" 
                  />
                </div>
                <div className="p-6 sm:w-2/3 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="w-5 h-5 text-[#9D4EDD]" />
                    <h3 className="text-lg font-bold text-[#E0E0E0]">Legendz Casino</h3>
                  </div>
                  <p className="text-[#39FF14] text-sm font-medium mb-2">Social Casino Platform</p>
                  <p className="text-gray-400 text-sm">Official brand ambassador promoting gameplay and community engagement.</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="glass-card overflow-hidden">
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-1/3 aspect-square sm:aspect-auto">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ugc-marketing-brand/artifacts/vy6scncj_52e601cad9acb2b998aaf11a64b567c2_0.jpeg" 
                    alt="PlayFame" 
                    className="w-full h-full object-cover" 
                  />
                </div>
                <div className="p-6 sm:w-2/3 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="w-5 h-5 text-[#9D4EDD]" />
                    <h3 className="text-lg font-bold text-[#E0E0E0]">PlayFame</h3>
                  </div>
                  <p className="text-[#39FF14] text-sm font-medium mb-2">Social Gaming Platform</p>
                  <p className="text-gray-400 text-sm">Content creator and affiliate partner driving community growth.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section 
          variants={containerVariants} 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="glass-card p-12 text-center border-[#9D4EDD]/30">
            <div className="w-20 h-20 rounded-full bg-[#9D4EDD]/20 flex items-center justify-center mx-auto mb-6">
              <Handshake className="w-10 h-10 text-[#9D4EDD]" />
            </div>
            
            <h2 className="text-2xl sm:text-3xl font-bold uppercase mb-4">
              <span className="text-[#E0E0E0]">Lets Work</span>{' '}
              <span className="text-[#9D4EDD]">Together</span>
            </h2>
            
            <p className="text-gray-400 max-w-2xl mx-auto mb-8">
              Ready to reach an engaged audience of social casino enthusiasts? 
              I would love to discuss how we can create a partnership that benefits both your brand and my community.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary px-8 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2"
                data-testid="partner-contact-btn"
              >
                Start a Partnership
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://www.instagram.com/relatableshayla?igsh=bnR1Z3ZzbzZkMnU2"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost px-8 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2"
                data-testid="partner-instagram-btn"
              >
                DM on Instagram
              </a>
            </div>
          </motion.div>
        </motion.section>
      </div>
    </div>
  );
};

export default AffiliatePage;
